import React, { useEffect, useState } from "react";
import request from "../../service/request";
import { Helmet } from 'react-helmet';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Select from 'react-select'; // Import React Select
import { Modal, Nav, Row, Col } from 'react-bootstrap';

const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: "smooth",
    });
};

const AdminInquiryForm = () => {
    const [menu, setMenu] = useState([]);
    const [config, setConfig] = useState({});
    const [dropdowns, setDropdowns] = useState({
        property_types: [],
        property_fors: [],
        property_sources: [],
        available_fors: [],
        furnished_statuses: [],
        agents: [],
        property_configurations: [],
        specific_properties: [],
        building_types: [],
        building_statuses: [],
        countries: [],
        states: [],
        cities: [],
        areas: [],
        area_measurements: [],
        projects: [],
        facings: [],
        loan_requirements: [],
        land_types: [],
        mobile_codes: [],
        defaults: {}
    });
    const [loading, setLoading] = useState(true);
    const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

    // WhatsApp Share State
    const [whatsappData, setWhatsappData] = useState({ number: '', templates: [] });
    const [showWhatsappModal, setShowWhatsappModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    // State for dependent dropdowns
    const [filteredSpecificProperties, setFilteredSpecificProperties] = useState([]);
    const [filteredPropertyConfigs, setFilteredPropertyConfigs] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);
    const [filteredAreas, setFilteredAreas] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Config
                const configRes = await request({ method: 'get', url: 'get-admin-inquiry-form-config' });
                if (configRes.data.code === 1 || configRes.data.code === 0) {
                    setConfig(configRes.data.data);
                }

                // Fetch Dropdowns
                const dropdownRes = await request({ method: 'get', url: 'get-inquiry-dropdowns' });
                if (dropdownRes.data.code === 1) {
                    const data = dropdownRes.data.data;
                    setDropdowns(data);

                    // Initialize Dependent Dropdowns if defaults exist or pre-filled
                    if (data.defaults?.state_id) {
                        setFilteredCities(data.cities.filter(c => c.state_id == data.defaults.state_id));
                    } else {
                        setFilteredCities(data.cities);
                    }
                    if (data.defaults?.city_id) {
                        setFilteredAreas(data.areas.filter(a => a.city_id == data.defaults.city_id));
                    } else {
                        setFilteredAreas(data.areas);
                    }
                }

            } catch (error) {
                console.error("Error fetching data:", error);
                setSubmitStatus({ type: 'danger', message: 'Failed to load form data. Please refresh.' });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        scrollToTop();
    }, []);

    useEffect(() => {
        request({
            method: 'get',
            url: '/get-menu',
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
        })
            .then((response) => {
                setMenu(response?.data);
            })
            .catch(({ data }) => ({ error: data }));
    }, []);

    const phoneRegExp = /^[0-9]{8,12}$/;

    // Multi-select fields list
    const multiSelectFields = ['specific_property', 'configuration', 'city', 'area_locality', 'facing', 'furnished_status', 'preferred_project'];

    // Dynamic validation schema
    const validationSchema = yup.lazy(values => {
        let schemaShape = {
            // Static Personal Fields
            ...(config.client_name ? { client_name: yup.string().required(`${config.client_name} is required`).min(2, "Name must be at least 2 characters") } : {}),
            ...(config.client_mobile ? { client_mobile: yup.string().matches(phoneRegExp, 'Phone number must be between 8 and 12 digits').required(`${config.client_mobile} is required`) } : {}),
            ...(config.client_email ? { client_email: yup.string().email("Invalid email").nullable() } : {}),
            ...(config.client_alt_mobile ? { client_alt_mobile: yup.string().matches(phoneRegExp, 'Phone number must be between 8 and 12 digits').nullable() } : {}),
        };

        const requiredFields = ['property_type', 'property_for', 'assign_to'];

        Object.keys(config).forEach(key => {
            if (['client_name', 'client_mobile', 'client_email', 'client_alt_mobile'].includes(key)) return;

            if (requiredFields.includes(key)) {
                if (multiSelectFields.includes(key)) {
                    schemaShape[key] = yup.array().min(1, `${config[key]} is required`).nullable();
                } else {
                    schemaShape[key] = yup.string().required(`${config[key]} is required`);
                }
            } else {
                if (multiSelectFields.includes(key)) {
                    schemaShape[key] = yup.array().nullable();
                } else {
                    schemaShape[key] = yup.string().nullable();
                }
            }
        });

        // Custom Validation for Range Fields
        const greaterThanFrom = (fromKey, label) => {
            return function (val) {
                const fromVal = this.parent[fromKey];
                if (!fromVal || !val) return true;
                return parseFloat(val) > parseFloat(fromVal);
            };
        };

        const notOnlyZero = (msg) => {
            return (val) => !val || parseFloat(val) > 0;
        };

        if (config.area_size_from) schemaShape.area_size_from = yup.string().test('not-zero', 'Value must be greater than 0', notOnlyZero()).nullable();
        if (config.area_size_to) schemaShape.area_size_to = yup.string().test('not-zero', 'Value must be greater than 0', notOnlyZero()).test('greater', 'Area To must be greater than From', greaterThanFrom('area_size_from')).nullable();

        if (config.budget_from) schemaShape.budget_from = yup.string().test('not-zero', 'Value must be greater than 0', notOnlyZero()).nullable();
        if (config.budget_to) schemaShape.budget_to = yup.string().test('not-zero', 'Value must be greater than 0', notOnlyZero()).test('greater', 'Budget To must be greater than From', greaterThanFrom('budget_from')).nullable();

        return yup.object().shape(schemaShape);
    });

    const formik = useFormik({
        initialValues: {
            client_name: '',
            client_mobile: '',
            client_mobile_code: '+91',
            client_email: '',
            client_alt_mobile: '',
            client_alt_mobile_code: '+91',
            property_type: '',
            property_for: '',
            inquiry_source: '',
            preferred_project: [],
            facing: [],
            land_type: '',
            loan_requirements: '',
            area_measurement: dropdowns.area_measurements?.[0]?.id || 1,
            // Initialize others
            ...Object.keys(config).reduce((acc, key) => {
                if (!['client_name', 'client_mobile', 'client_email', 'client_alt_mobile', 'property_type', 'property_for', 'inquiry_source', 'preferred_project', 'facing', 'land_type', 'loan_requirements', 'area_measurement'].includes(key)) {
                    if (multiSelectFields.includes(key)) {
                        acc[key] = [];
                    } else {
                        acc[key] = '';
                    }
                }
                return acc;
            }, {})
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, { resetForm }) => {
            setSubmitStatus({ type: '', message: '' });
            request({
                method: 'post',
                url: 'inquiry/store-from-admin-form',
                data: values
            })
                .then((response) => {
                    if (response.data.code === 0 || response.data.code === 'success') {
                        setSubmitStatus({ type: 'success', message: 'Inquiry saved successfully!' });

                        // Handle WhatsApp Data
                        if (response.data.whatsapp_number && response.data.whatsapp_templates) {
                            setWhatsappData({
                                number: response.data.whatsapp_number,
                                templates: response.data.whatsapp_templates
                            });
                            // Select first template by default if available
                            if (response.data.whatsapp_templates.length > 0) {
                                setSelectedTemplate(response.data.whatsapp_templates[0]);
                            }
                        }

                        resetForm();
                        scrollToTop();
                    } else {
                        setSubmitStatus({ type: 'danger', message: response.data.message || 'Failed to save inquiry' });
                    }
                })
                .catch((error) => {
                    setSubmitStatus({ type: 'danger', message: 'An error occurred. Please try again.' });
                });
        }
    });

    const handleWhatsappSend = () => {
        if (selectedTemplate && whatsappData.number) {
            const url = `https://api.whatsapp.com/send?phone=${whatsappData.number}&text=${selectedTemplate.whatsapp_text}`;
            window.open(url, '_blank');
        }
    };

    // Handle dependent field changes
    const handleFieldChange = (key, value) => {
        formik.setFieldValue(key, value);

        if (key === 'property_type') {
            const pType = parseInt(value);
            const specificProps = dropdowns.specific_properties.filter(item => item.property_type_id === pType);
            const propConfigs = dropdowns.property_configurations.filter(item => item.property_type_id === pType);

            setFilteredSpecificProperties(specificProps);
            setFilteredPropertyConfigs(propConfigs);

            formik.setFieldValue('specific_property', []);
            formik.setFieldValue('configuration', []);
        }

        if (key === 'state') {
            const stateId = parseInt(value);
            const cities = dropdowns.cities.filter(item => item.state_id === stateId);
            setFilteredCities(cities);
            setFilteredAreas([]);
            formik.setFieldValue('city', []);
            formik.setFieldValue('area_locality', []);
        }

        if (key === 'city') {
            const cityIds = Array.isArray(value) ? value : [value];
            const areas = dropdowns.areas.filter(item => cityIds.includes(item.city_id));
            setFilteredAreas(areas);

            if (formik.values.area_locality) {
                const currentLocIds = formik.values.area_locality || [];
                const validLocIds = currentLocIds.filter(id => areas.some(a => a.id === id));
                formik.setFieldValue('area_locality', validLocIds);
            }
        }
    };

    const renderField = (key, label, isRequired) => {
        let options = [];
        let isSelect = false;

        switch (key) {
            case 'property_type': isSelect = true; options = dropdowns.property_types; break;
            case 'property_for':
                isSelect = true;
                options = dropdowns.property_fors.map(opt => (opt.name === 'Sell' ? { ...opt, name: 'Buy' } : opt));
                break;
            case 'assign_to': isSelect = true; options = dropdowns.agents; break;
            case 'inquiry_source': isSelect = true; options = dropdowns.property_sources; break;
            case 'available_for': isSelect = true; options = dropdowns.available_fors; break;
            case 'furnished_status': isSelect = true; options = dropdowns.furnished_statuses; break;
            case 'building_type_id': isSelect = true; options = dropdowns.building_types; break;
            case 'building_status': isSelect = true; options = dropdowns.building_statuses; break;
            case 'specific_property': isSelect = true; options = filteredSpecificProperties.length > 0 ? filteredSpecificProperties : dropdowns.specific_properties; break;
            case 'configuration': isSelect = true; options = filteredPropertyConfigs.length > 0 ? filteredPropertyConfigs : dropdowns.property_configurations; break;
            case 'state': isSelect = true; options = dropdowns.states; break;
            case 'city': isSelect = true; options = filteredCities.length > 0 ? filteredCities : (formik.values.state ? [] : dropdowns.cities); break;
            case 'area_locality':
                isSelect = true; options = filteredAreas.length > 0 ? filteredAreas.map(a => ({ id: a.id, name: a.area || a.name })) : (formik.values.city && formik.values.city.length > 0 ? [] : dropdowns.areas.map(a => ({ id: a.id, name: a.area || a.name }))); break;
            case 'preferred_project': isSelect = true; options = dropdowns.projects; break;
            case 'facing': isSelect = true; options = dropdowns.facings; break;
            case 'loan_requirements': isSelect = true; options = dropdowns.loan_requirements; break;
            case 'land_type': isSelect = true; options = dropdowns.land_types; break;

            default: isSelect = false;
        }

        if (isSelect) {
            // Prepare options for React Select {value, label}
            const selectOptions = options.map(opt => ({ value: opt.id, label: opt.name || opt.area || opt.measurement || opt.value })); // Adjust based on data structure

            if (multiSelectFields.includes(key)) {

                // value prop for MultiSelect
                const selectedValue = selectOptions.filter(opt => (formik.values[key] || []).includes(opt.value));

                return (
                    <div className="col-12 form-group" key={key} style={{ marginBottom: '20px' }}>
                        <label className="text-muted font-weight-bold mb-1 d-block text-left">
                            {label} {isRequired && <span className="text-danger">*</span>}
                        </label>
                        <Select
                            isMulti
                            className="basic-multi-select"
                            classNamePrefix="select"
                            options={selectOptions}
                            value={selectedValue}
                            onChange={(selected) => handleFieldChange(key, selected ? selected.map(s => s.value) : [])}
                            placeholder={`Select ${label}...`}
                            styles={{
                                control: (base) => ({ ...base, minHeight: '38px' }),
                                menu: (base) => ({ ...base, zIndex: 9999 })
                            }}
                        />
                        {formik.touched[key] && formik.errors[key] && (
                            <div className="text-danger small mt-1 ml-1">{formik.errors[key]}</div>
                        )}
                    </div>
                );
            }

            // Single Select Logic
            const selectedValue = selectOptions.find(opt => opt.value == formik.values[key]) || null;

            return (
                <div className="col-12 form-group" key={key} style={{ marginBottom: '20px' }}>
                    <label className="text-muted font-weight-bold mb-1 d-block text-left">
                        {label} {isRequired && <span className="text-danger">*</span>}
                    </label>
                    <Select
                        className="basic-single-select"
                        classNamePrefix="select"
                        options={selectOptions}
                        value={selectedValue}
                        onChange={(selected) => handleFieldChange(key, selected ? selected.value : '')}
                        placeholder={`Select ${label}...`}
                        styles={{
                            control: (base) => ({ ...base, minHeight: '38px' }),
                            menu: (base) => ({ ...base, zIndex: 9999 })
                        }}
                    />
                    {formik.touched[key] && formik.errors[key] && (
                        <div className="text-danger small mt-1 ml-1">{formik.errors[key]}</div>
                    )}
                </div>
            );
        }

        if (key === 'client_mobile' || key === 'client_alt_mobile') {
            const codeKey = key === 'client_mobile' ? 'client_mobile_code' : 'client_alt_mobile_code';
            return (
                <div className="col-12 form-group" key={key} style={{ marginBottom: '20px' }}>
                    <label className="text-muted font-weight-bold mb-1 d-block text-left">
                        {label} {isRequired && <span className="text-danger">*</span>}
                    </label>
                    <div className="input-group" style={{ width: '100%' }}>
                        <div className="input-group-prepend">
                            <select
                                className="form-control"
                                name={codeKey}
                                value={formik.values[codeKey]}
                                onChange={formik.handleChange}
                                style={{ height: '38px', minWidth: '80px', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                            >
                                {dropdowns.mobile_codes.map(code => (
                                    <option key={code} value={code}>{code}</option>
                                ))}
                            </select>
                        </div>
                        <input
                            type="text"
                            name={key}
                            maxLength={12}
                            className={`form-control ${formik.touched[key] && formik.errors[key] ? 'is-invalid' : ''}`}
                            placeholder={`Enter ${label}`}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (/^\d*$/.test(val)) {
                                    formik.setFieldValue(key, val);
                                }
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values[key]}
                            style={{ height: '38px' }}
                        />
                    </div>
                    {formik.touched[key] && formik.errors[key] && (
                        <div className="invalid-feedback d-block">{formik.errors[key]}</div>
                    )}
                </div>
            );
        }

        if (key === 'area_size_from' || key === 'area_size_to') {
            return (
                <div className="col-12 form-group" key={key} style={{ marginBottom: '20px' }}>
                    <label className="text-muted font-weight-bold mb-1 d-block text-left">
                        {label} {isRequired && <span className="text-danger">*</span>}
                    </label>
                    <div className="input-group" style={{ width: '100%' }}>
                        <input
                            type="text"
                            name={key}
                            className={`form-control ${formik.touched[key] && formik.errors[key] ? 'is-invalid' : ''}`}
                            placeholder={`Enter ${label}`}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || /^[1-9][0-9]*$/.test(val)) {
                                    formik.setFieldValue(key, val);
                                }
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values[key]}
                            style={{ height: '38px' }}
                        />
                        <div className="input-group-append">
                            <select
                                className="form-control"
                                name="area_measurement"
                                value={formik.values.area_measurement}
                                onChange={formik.handleChange}
                                style={{ height: '38px', minWidth: '80px', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                            >
                                {dropdowns.area_measurements.map(m => (
                                    <option key={m.id} value={m.id}>{m.measurement}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {formik.touched[key] && formik.errors[key] && (
                        <div className="invalid-feedback d-block">{formik.errors[key]}</div>
                    )}
                </div>
            );
        }

        if (key === 'budget_from' || key === 'budget_to') {
            return (
                <div className="col-12 form-group" key={key} style={{ marginBottom: '20px' }}>
                    <label className="text-muted font-weight-bold mb-1 d-block text-left">
                        {label} {isRequired && <span className="text-danger">*</span>}
                    </label>
                    <input
                        type="text"
                        name={key}
                        className={`form-control ${formik.touched[key] && formik.errors[key] ? 'is-invalid' : ''}`}
                        placeholder={`Enter ${label}`}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^[1-9][0-9]*$/.test(val)) {
                                formik.setFieldValue(key, val);
                            }
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values[key]}
                        style={{ height: '38px' }}
                    />
                    {formik.touched[key] && formik.errors[key] && (
                        <div className="invalid-feedback">{formik.errors[key]}</div>
                    )}
                </div>
            );
        }

        if (key === 'remarks') {
            return (
                <div className="col-12 form-group" key={key} style={{ marginBottom: '20px' }}>
                    <label className="text-muted font-weight-bold mb-1 d-block text-left">
                        {label} {isRequired && <span className="text-danger">*</span>}
                    </label>
                    <textarea
                        name={key}
                        rows="1"
                        className={`form-control ${formik.touched[key] && formik.errors[key] ? 'is-invalid' : ''}`}
                        placeholder={`Enter ${label}`}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[key]}
                    ></textarea>
                    {formik.touched[key] && formik.errors[key] && (
                        <div className="invalid-feedback">{formik.errors[key]}</div>
                    )}
                </div>
            );
        }

        return (
            <div className="col-12 form-group" key={key} style={{ marginBottom: '20px' }}>
                <label className="text-muted font-weight-bold mb-1 d-block text-left">
                    {label} {isRequired && <span className="text-danger">*</span>}
                </label>
                <input
                    type="text"
                    name={key}
                    className={`form-control ${formik.touched[key] && formik.errors[key] ? 'is-invalid' : ''}`}
                    placeholder={`Enter ${label}`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values[key]}
                    style={{ height: '38px' }}
                />
                {formik.touched[key] && formik.errors[key] && (
                    <div className="invalid-feedback">{formik.errors[key]}</div>
                )}
            </div>
        );
    };

    if (loading) return <div className="text-center py-5">Loading...</div>;

    const allKeys = Object.keys(config);

    return (
        <>
            <Helmet>
                <title>{`${menu.data?.site_setting?.site_name} - Inquiry Form`}</title>
            </Helmet>
            <main id="main">
                <section className="bg-gray" style={{ paddingTop: "5rem", paddingBottom: "5rem" }}>
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-10">
                                <div className="card shadow-lg border-0 rounded-lg">
                                    <div className="card-body p-4 p-md-5">
                                        <div className="text-center mb-5">
                                            <h3 style={{ color: "#333", fontWeight: "700" }}>Inquiry Form</h3>
                                            <p className="text-muted">Fill in the inquiry details</p>
                                        </div>

                                        {submitStatus.message && (
                                            <div className={`alert alert-${submitStatus.type} shadow-sm border-0 mb-4`} role="alert">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div>
                                                        {submitStatus.type === 'success' ? <i className="fa fa-check-circle mr-2"></i> : <i className="fa fa-exclamation-circle mr-2"></i>}
                                                        {submitStatus.message}
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        {submitStatus.type === 'success' && whatsappData.templates.length > 0 && (
                                                            <button
                                                                className="btn-custome text-white mr-3"
                                                                type="button"
                                                                style={{ backgroundColor: "#28a745", borderColor: "#28a745", borderRadius: ".25rem" }}
                                                                onClick={() => setShowWhatsappModal(true)}
                                                            >
                                                                <i className="fab fa-whatsapp mr-1"></i> Share on WhatsApp
                                                            </button>
                                                        )}
                                                        <button type="button" className="close" onClick={() => setSubmitStatus({ type: '', message: '' })}>
                                                            <span aria-hidden="true">&times;</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <form onSubmit={formik.handleSubmit}>
                                            <div className="row">
                                                {allKeys.map(key => {
                                                    if (!config[key]) return null;

                                                    const isLand = formik.values.property_type == 4;

                                                    if (key === 'land_type' && !isLand) return null;
                                                    if ((key === 'budget_from' || key === 'budget_to') && isLand) return null;

                                                    const isRequired = ['client_name', 'client_mobile', 'property_type', 'property_for', 'assign_to'].includes(key);
                                                    return renderField(key, config[key], isRequired);
                                                })}
                                            </div>

                                            <div className="row mt-5">
                                                <div className="col-12 d-flex justify-content-center">
                                                    <button type="submit" className="btn text-white px-5 py-2" style={{ fontSize: '1.2rem' }}>
                                                        Submit Inquiry
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* WhatsApp Share Modal */}
            <Modal show={showWhatsappModal} onHide={() => setShowWhatsappModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Share on WhatsApp</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        {!!whatsappData.templates && whatsappData.templates.length > 0 ? (
                            <>
                                <Col md={3} className="border-right">
                                    <Nav variant="pills" className="flex-column">
                                        {whatsappData.templates.map((template) => (
                                            <Nav.Item key={template.id}>
                                                <Nav.Link
                                                    eventKey={template.id}
                                                    active={selectedTemplate && selectedTemplate.id === template.id}
                                                    onClick={() => setSelectedTemplate(template)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {template.name}
                                                </Nav.Link>
                                            </Nav.Item>
                                        ))}
                                    </Nav>
                                </Col>
                                <Col md={9}>
                                    {selectedTemplate ? (
                                        <div className="p-3 border rounded bg-light" style={{ minHeight: '150px' }}>
                                            <div dangerouslySetInnerHTML={{ __html: selectedTemplate.description }} />
                                        </div>
                                    ) : (
                                        <div className="text-muted text-center p-5">Select a template</div>
                                    )}
                                </Col>
                            </>
                        ) : (
                            <Col xs={12} className="text-center text-danger">No templates found!</Col>
                        )}
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    {/* <button className="btn-custome text-white" onClick={() => setShowWhatsappModal(false)} style={{ backgroundColor: "#6c757d", borderColor: "#6c757d", borderRadius: ".25rem" }}>
                        Close
                    </button> */}
                    <button className="btn-custome text-white" onClick={handleWhatsappSend} disabled={!selectedTemplate} style={{ backgroundColor: "#28a745", borderColor: "#28a745", borderRadius: ".25rem" }}>
                        <i className="fab fa-whatsapp mr-1"></i> Send
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AdminInquiryForm;
